/**
 * RedFlagChip — badge + popover surfacing the AI red-flag scan result on an
 * employment document item.
 *
 * States:
 *   verified   → 🟢 emerald "Verified"
 *   minor      → 🟡 amber "Minor mismatch"
 *   red_flag   → 🔴 rose "Red flag"
 *   unreadable → ⚪ slate "Unreadable"
 *   scanning   → ⏳ slate spinner "Scanning…"
 *   (none)     → hidden
 *
 * Clicking the chip opens a popover listing findings + extracted fields, and
 * a "Rescan" button that POSTs the manual endpoint.
 */
import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ShieldAlert, ShieldCheck, AlertTriangle, HelpCircle, Loader2, RefreshCw } from "lucide-react";

const STATUS_META = {
    verified: { label: "Verified", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
    minor:    { label: "Minor",    cls: "bg-amber-50 text-amber-800 border-amber-200",       Icon: AlertTriangle },
    red_flag: { label: "Red flag", cls: "bg-rose-50 text-rose-700 border-rose-200",           Icon: ShieldAlert },
    unreadable: { label: "Unreadable", cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: HelpCircle },
    scanning: { label: "Scanning", cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: Loader2 },
};

export default function RedFlagChip({ scan, candidateId, expKey, slot, onRescan }) {
    const [busy, setBusy] = useState(false);
    if (!scan || !scan.status) return null;
    const meta = STATUS_META[scan.status] || STATUS_META.minor;
    const Icon = meta.Icon;

    const rescan = async () => {
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/experience-documents/${encodeURIComponent(expKey)}/${slot}/scan`);
            toast.success("Scan complete");
            onRescan?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`red-flag-chip-${slot}`}
                    className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${meta.cls} hover:opacity-80`}
                    title={`AI verification: ${meta.label}`}
                >
                    <Icon className={`w-3 h-3 ${scan.status === "scanning" ? "animate-spin" : ""}`} />
                    {meta.label}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3 space-y-2" align="end" data-testid={`red-flag-popover-${slot}`}>
                <div className="flex items-center justify-between gap-2">
                    <div className={`inline-flex items-center gap-1 text-xs font-semibold ${scan.status === "red_flag" ? "text-rose-700" : scan.status === "verified" ? "text-emerald-700" : "text-slate-700"}`}>
                        <Icon className={`w-4 h-4 ${scan.status === "scanning" ? "animate-spin" : ""}`} />
                        AI verification — {meta.label}
                    </div>
                    <button
                        type="button"
                        onClick={rescan}
                        disabled={busy}
                        className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 hover:bg-secondary inline-flex items-center gap-1"
                        data-testid={`red-flag-rescan-${slot}`}
                        title="Re-run the AI scan"
                    >
                        <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
                        {busy ? "Scanning" : "Rescan"}
                    </button>
                </div>
                {scan.findings?.length > 0 ? (
                    <ul className="text-xs space-y-1 list-disc pl-4">
                        {scan.findings.map((f, i) => (
                            <li key={i} data-testid={`red-flag-finding-${slot}-${i}`}>{f}</li>
                        ))}
                    </ul>
                ) : (
                    <div className="text-xs text-muted-foreground italic">No findings.</div>
                )}
                {(scan.employer_extracted || scan.title_extracted || scan.joining_date_extracted || scan.relieving_date_extracted) && (
                    <div className="border-t border-border pt-2 space-y-0.5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Extracted</div>
                        {scan.employer_extracted && (
                            <div className="text-xs"><span className="text-muted-foreground">Employer: </span>{scan.employer_extracted}</div>
                        )}
                        {scan.title_extracted && (
                            <div className="text-xs"><span className="text-muted-foreground">Title: </span>{scan.title_extracted}</div>
                        )}
                        {scan.joining_date_extracted && (
                            <div className="text-xs"><span className="text-muted-foreground">Joining: </span>{scan.joining_date_extracted}</div>
                        )}
                        {scan.relieving_date_extracted && (
                            <div className="text-xs"><span className="text-muted-foreground">Relieving: </span>{scan.relieving_date_extracted}</div>
                        )}
                    </div>
                )}
                {scan.run_at && (
                    <div className="text-[10px] text-muted-foreground">
                        Scanned {new Date(scan.run_at).toLocaleString()}
                        {scan.model && ` · ${scan.model}`}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
